/**
 * 현장 3D 뷰어에 마커로 표시되는 디바이스.
 *
 * 현재는 CCTV만 실데이터(API)로 연동되고, 온도/가스/스마트밴드/방송 등은
 * 백엔드 디바이스 API가 준비되는 대로 하나씩 추가된다. 타입/아이콘 체계는
 * 그 확장을 미리 수용하도록 generic 하게 유지한다.
 */
export type DeviceType = "cctv" | "thermo" | "gas" | "band" | "broadcast";

export interface DeviceStatus {
  /** 정상 | 주의 | 심각 — 마커 링 색을 결정 */
  level: "normal" | "warning" | "critical";
  /** 라벨 보조 정보 (체온/농도 등). 없으면 미표시 */
  meta?: string;
}

/** 3D 마커로 렌더되는 디바이스. lon/lat이 있어야 배치 가능. */
export interface SiteDevice {
  id: string;
  type: DeviceType;
  name: string;
  lon: number;
  lat: number;
  /** 모델 좌표계 y (m). floor 분류는 model-layers.findFloorForY로 자동 계산. */
  altitude: number;
  status: DeviceStatus;
}

// ── 표시 메타데이터 (마커 텍스처 + 정보 패널이 공유) ──────────────────────────
export const DEVICE_TYPE_LABEL: Record<DeviceType, string> = {
  cctv: "CCTV",
  thermo: "온도",
  gas: "가스",
  band: "밴드",
  broadcast: "방송",
};

export const DEVICE_TYPE_COLOR: Record<DeviceType, string> = {
  cctv: "#388bfd",
  thermo: "#22c55e",
  gas: "#f59e0b",
  band: "#a855f7",
  broadcast: "#ec4899",
};

export const STATUS_COLOR: Record<DeviceStatus["level"], string> = {
  normal: "#22c55e",
  warning: "#f59e0b",
  critical: "#ef4444",
};

export const STATUS_LABEL: Record<DeviceStatus["level"], string> = {
  normal: "정상",
  warning: "주의",
  critical: "심각",
};

/** 선택 강조 색 (마커 링 + 정보 패널 액센트). */
export const SELECTED_COLOR = "#ff7500";

/** CCTV alt 미지정 시 사용할 기본 설치 높이 (m). */
const DEFAULT_CCTV_ALT = 6;

/** cctvToDevice 입력 — SafersCCTV 중 매핑에 필요한 필드만 (전체 API 타입에 결합 방지). */
export interface CCTVInput {
  id: number;
  name: string;
  lon?: number;
  lat?: number;
  alt?: number;
}

/**
 * CCTV → SiteDevice 매핑.
 * - lon/lat 없는 CCTV는 3D 배치 불가 → null (호출부에서 필터).
 * - status는 백엔드에 아직 필드가 없어 normal 고정. 디바이스 API 연동 시 확장.
 */
export function cctvToDevice(cctv: CCTVInput): SiteDevice | null {
  if (cctv.lon == null || cctv.lat == null) return null;
  return {
    id: `cctv-${cctv.id}`,
    type: "cctv",
    name: cctv.name,
    lon: cctv.lon,
    lat: cctv.lat,
    altitude: cctv.alt ?? DEFAULT_CCTV_ALT,
    status: { level: "normal" },
  };
}
